import { YoutubeFileIcon } from "@images/svgIcons/medias/youtubeFileIcon"
import { CloseIcon } from "@images/svgIcons/navigation/closeIcon"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { word } from "@modules/core/utils/i18n"
import { FeatureKeys } from "@modules/featureToggle/featureToggleTypes"
import { useFeatureToggle } from "@modules/featureToggle/hooks/useFeatureToggle"
import { MediaExistingBlockImage } from "@modules/sessionBlock/media/components/mediaExistingBlocImage"
import { MediaExistingBlockPdf } from "@modules/sessionBlock/media/components/mediaExistingBlocPdf"
import { MediaExistingBlockVideo } from "@modules/sessionBlock/media/components/mediaExistingBlocVideo"
import { MediaExistingBlockYoutube } from "@modules/sessionBlock/media/components/mediaExistingBlocYoutube"
import { MediaItemFormikType } from "@modules/sessionBlock/media/mediaFormikTypes"
import { MediaType } from "@modules/sessionBlock/media/mediaTypes"
import { VIDEO_MAX_SIZE } from "@modules/ui/components/fileUpload/fileUploadUtils"
import { useUploadMedia } from "@modules/ui/components/fileUpload/hooks/useUploadMedia"
import { MediaUploadDragZone } from "@modules/ui/components/fileUpload/mediaUploadDragZone"
import { HUButton } from "@modules/ui/components/huButton"
import { HUInput } from "@modules/ui/components/huInput"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { LoadingPoints } from "@modules/ui/components/loading"
import { isYoutubeLink } from "@modules/utils/convertYoutubeLink"
import { CheckIcon } from "lucide-react"
import { FileUploadProps, FileUploadSelectEvent, FileUpload as PRFileUpload } from "primereact/fileupload"
import { useRef, useState } from "react"
import { useTheme } from "styled-components"

type MediaUploadProps = {
  mediaList: MediaItemFormikType[]
  updateMediaList: (mediaList: MediaItemFormikType[]) => void
  updateMediaItem: (mediaItem: MediaItemFormikType, index: number) => void
  name: string
} & React.ComponentProps<"div"> &
  FileUploadProps

export function MediaUpload({ name, mediaList = [], updateMediaList, updateMediaItem, ...props }: MediaUploadProps) {
  const [displayYoutubeUrlInput, setDisplayYoutubeUrlInput] = useState(false)
  const [youtubeUrl, setYoutubeUrl] = useState("")

  const canUseMultipleMedias = useFeatureToggle(FeatureKeys.MultipleMedias)
  const { uploadMedia } = useUploadMedia()
  const toast = useToast()
  const theme = useTheme()

  const fileUploadRef = useRef<PRFileUpload>(null)

  const openFileSelector = () => {
    fileUploadRef.current?.getInput().click()
  }

  const onSelectFile = async (e: FileUploadSelectEvent) => {
    try {
      e.files.forEach(async (mediaFile: File) => {
        if (mediaFile.size > VIDEO_MAX_SIZE) {
          toast.show({
            severity: "error",
            summary: word("global.error.label"),
            detail: word("video_upload.max_size.error", { maxSize: VIDEO_MAX_SIZE / 1_000_000 }),
          })
          fileUploadRef.current?.clear()
          return
        }

        if (mediaFile) {
          await onUploadNewMedia(mediaFile)
        } else {
          toast.show({
            severity: "error",
            summary: word("global.error.label"),
            detail: word("global.fileUpload.error"),
          })
        }
      })
    } catch (e: any) {
      toast.show({
        severity: "error",
        summary: word("global.error.label"),
        detail: word("global.fileUpload.error"),
      })
      console.error(`[MediaUpload] ${e.message ?? word("global.error.label")}`)
    }

    fileUploadRef.current?.clear()
  }

  const onUploadNewMedia = async (file: File) => {
    if (!canUseMultipleMedias) {
      if (file.type.indexOf("video/") < 0 || mediaList.length > 0) return
    } else {
      if (file.type.indexOf("video/") < 0 && file.type.indexOf("image/") < 0 && file.type !== "application/pdf") return
    }

    const newMedia: MediaItemFormikType = {
      id: null,
      url: "",
      file,
      awsS3Key: "",
      type:
        file.type.indexOf("video/") >= 0
          ? MediaType.S3_VIDEO
          : file.type === "application/pdf"
            ? MediaType.S3_PDF
            : MediaType.S3_IMAGE,
      isUploading: true,
      filename: file.name,
    }

    const newItemIndex = mediaList.length

    updateMediaList([...mediaList, newMedia])

    uploadMedia(file)
      .then((response) => {
        const newMediaItem = {
          ...newMedia,
          awsS3Key: response.awsS3Key,
          isUploading: false,
        }

        updateMediaItem(newMediaItem, newItemIndex)
      })
      .catch((error) => {
        console.error(`[MediaUpload] ${error.message ?? word("global.error.label")}`)
        toast.show({
          severity: "error",
          summary: word("global.error.label"),
          detail: word("global.fileUpload.error"),
        })

        removeMediaItem(newItemIndex)
      })
  }

  const removeMediaItem = (index: number) => {
    const newMediaList = [...mediaList]
    newMediaList.splice(index, 1)
    updateMediaList(newMediaList)
  }

  const onAddYoutubeLink = () => {
    if (!youtubeUrl || youtubeUrl.length === 0 || !isYoutubeLink(youtubeUrl)) {
      toast.show({
        severity: "error",
        summary: word("global.error.label"),
        detail: word("sessionForm.blocks.add_media.youtube_error"),
      })
      return
    }

    const newMedia: MediaItemFormikType = {
      id: null,
      url: youtubeUrl,
      type: MediaType.YOUTUBE,
      filename: null,
    }

    updateMediaList([...mediaList, newMedia])
    setYoutubeUrl("")
    setDisplayYoutubeUrlInput(false)
  }

  return (
    <div {...props}>
      <div className="flex flex-column flex-grow-1 rounded-big-radius overflow-hidden border-300 border-1">
        {displayYoutubeUrlInput ? (
          <div className="bg-neutralColor-100 p-3 flex flex-row align-items-center justify-content-start gap-3">
            <HUInput
              placeholder={word("sessionForm.blocks.add_media.youtube_link.placeholder")}
              value={youtubeUrl}
              onChange={(e) => {
                setYoutubeUrl(e.currentTarget.value)
              }}
              className="flex-grow-1"
              inputClassName="bg-transparent"
              style={{ height: 48 }}
            />

            <HUButton
              size="M"
              colorType="Primary"
              type="Rounded"
              icon={{ icon: CheckIcon, iconStyle: { height: 20, width: 20 } }}
              style={{ height: 48, width: 48 }}
              onClick={onAddYoutubeLink}
            />
            <HUButton
              size="M"
              colorType="Quaternary"
              type="Rounded"
              icon={{ icon: CloseIcon, iconStyle: { height: 16, width: 16 } }}
              className="border-transparent"
              style={{ height: 48, width: 48 }}
              onClick={() => {
                setDisplayYoutubeUrlInput(false)
                setYoutubeUrl("")
              }}
            />
          </div>
        ) : (
          <div className="bg-neutralColor-100 p-3 flex flex-row align-items-center justify-content-start gap-3">
            <HUButton
              colorType="Tertiary"
              type="Left_Icon"
              icon={{
                iconView: <PlusIcon color={theme.textPrimary} height={24} />,
              }}
              className="px-3 py-2"
              size="XS"
              onClick={openFileSelector}
              text={word("sessionForm.blocks.add_media.upload")}
              disabled={!canUseMultipleMedias && mediaList.length > 0}
            />
            <HUButton
              colorType="Tertiary"
              type="Left_Icon"
              icon={{
                iconView: <YoutubeFileIcon style={{ height: 24 }} color={theme.textPrimary} />,
              }}
              className="px-3 py-2"
              size="XS"
              onClick={() => setDisplayYoutubeUrlInput(true)}
              text={word("sessionForm.blocks.add_media.add_youtube_link")}
              disabled={!canUseMultipleMedias && mediaList.length > 0}
            />
          </div>
        )}

        <div className="border-300 border-bottom-none border-left-none border-right-none border-top-1 border-dotted">
          <PRFileUpload
            ref={fileUploadRef}
            name={name}
            accept={canUseMultipleMedias ? "image/jpeg,image/png,video/mp4,video/quicktime,application/pdf" : "video/*"}
            headerClassName="hidden"
            contentClassName="border-none h-full p-0"
            emptyTemplate={() => (
              <MediaUploadDragZone
                openFileSelector={openFileSelector}
                disabled={!canUseMultipleMedias && mediaList.length > 0}
              />
            )}
            style={{ height: 240 }}
            onSelect={onSelectFile}
            multiple={canUseMultipleMedias}
            disabled={!canUseMultipleMedias && mediaList.length > 0}
          />
        </div>

        {mediaList.map((mediaItem, index) => (
          <div
            className="bg-neutralColor-100 border-300 border-bottom-none border-left-none border-right-none border-top-1 border-dotted px-3 flex flex-row align-items-center justify-content-between gap-3"
            style={{ height: 134 }}
            key={index}
          >
            {mediaItem.type === MediaType.S3_VIDEO ? (
              <MediaExistingBlockVideo key={index} media={mediaItem} />
            ) : mediaItem.type === MediaType.S3_IMAGE ? (
              <MediaExistingBlockImage
                key={index}
                media={mediaItem}
                style={{ maxHeight: 120, minHeight: "100%", height: 0, padding: "15px 0" }}
              />
            ) : mediaItem.type === MediaType.S3_PDF ? (
              <MediaExistingBlockPdf key={index} media={mediaItem} />
            ) : mediaItem.type === MediaType.YOUTUBE && mediaItem.url && isYoutubeLink(mediaItem.url) ? (
              <MediaExistingBlockYoutube key={index} media={mediaItem} />
            ) : (
              <HUText fontStyle="BM" style={{ overflowWrap: "break-word" }} key={index}>
                {word("sessionForm.blocks.add_media.missing_error")}
              </HUText>
            )}

            {mediaItem.isUploading ? (
              <LoadingPoints />
            ) : (
              <HUButton
                colorType="Quaternary"
                size="XS"
                type="Rounded"
                icon={{ icon: CloseIcon, iconStyle: { height: 16, width: 16 } }}
                className="border-transparent"
                onClick={() => removeMediaItem(index)}
                title={word("global.delete")}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
